<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import { slide } from 'svelte/transition';
	// Stores
	import BetTickerStore from '@/Stores/BetTickerStore.js';
	import PreferenceStore from '@/Stores/PreferenceStore.js';
	import AuthUserStore from '@/Stores/AuthUserStore.js';
	import AdminUserSavedFilterStore from '@/Stores/AdminUserSavedFilterStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	import URLStore from '@/Stores/UrlStore.js';
	// Services
	import {
		getUrlParameter,
		setUrlParameter,
		removeUrlParameter,
		paginationBuilder,
		extractMiddleNumber
	} from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import Polling from '@/Components/Polling.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Helmet from '@/Components/Helmet.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Partials
	import BetTickerRow from '@/Partials/Bets/_BetTickerRow.svelte';
	import Adjustment from '@/Partials/Bets/_Adjustment.svelte';
	import Cancel from '@/Partials/Bets/_Cancel.svelte';
	import ReviewPayout from '@/Partials/Bets/_ReviewPayout.svelte';
	import SaveFilter from '@/Partials/AdminUser/_SaveFilter.svelte';
	import ManualResult from '@/Partials/Sport/Markets/ManualResult/_ManualResult.svelte';
	import ManualResultSGM from '@/Partials/Sport/Markets/ManualResult/_ManualResultSGM.svelte';
	// Constants
	import { BetFilterMap, BetFilterSpecialLogic, BetFilterSpecialMap } from '@/Constants/Bet.js';
	import { BetTickerPeriodNames, BetTickerPeriods } from '@/Constants/Bet.js';
	// Config
	import Config from '@/config.js';

	export let showFilter = true;
	export let showSearch = true;
	export let defaultFilter = null;
	export let displayFormat = 'ticker';

	dayjs.extend(localizedFormat);

	const getTickerBets = async () => {
		if (searchBetTicket !== null) {
			return;
		}

		if (startDate || endDate) {
			updateDateFilter();
		}

		if (displayFormat === 'cashout') {
			return await BetTickerStore.getTickerBets(
				{ size: pageSize, number: pageNo },
				filter,
				{
					col: 'resulted_at',
					direction: 'desc'
				},
				'resulted_at'
			);
		}

		return await BetTickerStore.getTickerBets({ size: pageSize, number: pageNo }, filter);
	};

	let pageNo = getUrlParameter('page') || 1;
	let searchBetTicket = getUrlParameter('ticket') || null;
	let pageSize = 50;
	let searching = false;
	let startDate = getUrlParameter('start_date');
	let endDate = getUrlParameter('end_date');
	let filtering = false;
	let filter = defaultFilter ?? ($PreferenceStore.app.tickerFilter || {});
	let selectedDatePeriod = setInitialLoadDates();
	let showSaveFilter = false;
	let selectedSavedFilter = false;
	let loading = searchBetTicket !== null ? onSearchClick() : undefined;
	let showManualResult = false;
	let showManualResultSGM = false;
	let resultEventName;
	let resultMarket;
	let resultSelection;

	$: wantsCustomDateRange = false;
	$: links = $BetTickerStore.count() && paginationBuilder(BetTickerStore.getPagination());
	$: filterMeta = $BetTickerStore && BetTickerStore.getFilterMeta();
	$: bets = $BetTickerStore && filterBetStore();
	$: $AuthUserStore && getSavedFilters();
	$: selectedDatePeriod && setDatePeriod(selectedDatePeriod);
	$: (startDate || endDate) && pageSize && selectedSavedFilter && getTickerBets();
	$: selectedSavedFilter && setSavedFilter(selectedSavedFilter);
	$: $URLStore && processParamChanges();

	function processParamChanges() {
		if (searchBetTicket !== null) {
			if (getUrlParameter('ticket') == '') {
				searchBetTicket = null;
				loading = getTickerBets();
			}
		}
	}

	function setInitialLoadDates() {
		if (!startDate && !endDate) {
			if (ACLStore.isSuper() && Config.BUILD_ENVIRONMENT === 'development') {
				return BetTickerPeriods.Lifetime;
			}

			return BetTickerPeriods.Last7Days;
		}

		if (endDate) {
			return BetTickerPeriods.Custom;
		}

		if (startDate == dayjs().subtract(7, 'day').format('YYYY-MM-DD')) {
			return BetTickerPeriods.Last7Days;
		} else if (startDate == dayjs().subtract(30, 'day').format('YYYY-MM-DD')) {
			return BetTickerPeriods.Last30Days;
		} else if (startDate == dayjs().subtract(90, 'day').format('YYYY-MM-DD')) {
			return BetTickerPeriods.Last90Days;
		}

		return BetTickerPeriods.Custom;
	}

	function onSearchClick() {
		if (!searchBetTicket?.length) return;
		let searchValue = searchBetTicket;

		if (!searchValue.startsWith('1-')) {
			searchValue = '1-' + searchValue;
		}

		setUrlParameter('ticket', searchValue);
		pageNo = 1;
		removeUrlParameter('page');
		searching = true;
		return BetTickerStore.getTickerBets({ size: pageSize, number: pageNo }, { ticket: searchValue }).finally(
			() => (searching = false)
		);
	}

	function onClearSearchClick() {
		searchBetTicket = null;
		removeUrlParameter('ticket');
		loading = getTickerBets();
	}

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			getTickerBets();
			if (pageNo.toString() !== '1') {
				setUrlParameter('page', pageNo);
			} else {
				removeUrlParameter('page');
			}
		}
	}

	function filterBetStore() {
		return $BetTickerStore
			.reject((bet) => {
				// loop over every bet in store and filter out any not matching active filter
				// this can happen if we receive a bet via sockets, API polling will
				// refresh with the filter applied again. Multi's will always show.
				if (searchBetTicket !== null) {
					return bet.ticket !== (!searchBetTicket.startsWith('1-') ? '1-' + searchBetTicket : searchBetTicket);
				} else {
					for (const [key, value] of Object.entries(filter)) {
						// special case for min_bet_amount to be less than value
						if (key === 'min_bet_amount') {
							return parseInt(bet.bet_amount) < parseInt(value[0]);
						} else if (key === 'bet_filter') {
							if (value.toString().length) {
								// Check if this is a special filter and apply the appropriate logic
								for (const [filterKey, filterLogic] of Object.entries(BetFilterSpecialLogic)) {
									if (value.includes(filterKey) && BetFilterSpecialMap[filterKey]) {
										const betValue = BetFilterSpecialMap[filterKey]
											.split('.')
											.reduce((o, i) => o[i], bet)
											.toString();

										if (filterLogic(betValue, value)) {
											return true;
										}
									}
								}
							}
						} else {
							if (key !== 'from_date' && key !== 'to_date' && key !== 'reward_type') {
								if (value.toString().length) {
									if (
										value.indexOf(
											// allow for nested dot notation
											BetFilterMap[key]
												.split('.')
												.reduce((o, i) => o[i], bet)
												.toString()
										) < 0
									)
										return true;
								}
							}
						}
					}
				}
			})
			.sortByDesc(displayFormat === 'cashout' ? 'resulted_at' : 'created_at');
	}

	function updateFilter(e) {
		if (e.detail.name === 'min_bet_amount') {
			filter[e.detail.name] = [e.detail.checked[0]];
		} else {
			filter[e.detail.name] = e.detail.checked;
		}
	}

	function updateDateFilter() {
		if (startDate && typeof startDate !== 'undefined' && selectedDatePeriod !== BetTickerPeriods.Lifetime) {
			setUrlParameter('start_date', startDate);
			filter['from_date'] = startDate;
		} else {
			removeUrlParameter('start_date');
			delete filter['from_date'];
		}
		if (endDate && typeof endDate !== 'undefined') {
			setUrlParameter('end_date', endDate);
			filter['to_date'] = endDate;
		} else {
			removeUrlParameter('end_date');
			delete filter['to_date'];
		}
	}

	function applyFilter(resetPage = true) {
		filtering = true;
		updateDateFilter();

		if (resetPage) {
			pageNo = 1;
			removeUrlParameter('page');
		}
		if (showFilter) {
			PreferenceStore.updateSetting('app.tickerFilter', JSON.parse(JSON.stringify(filter)));
		}
		getTickerBets().then(() => {
			filtering = false;
		});
	}

	function resetFilter() {
		filtering = true;
		filter = {};
		pageNo = 1;
		removeUrlParameter('page');
		updateDateFilter();
		if (showFilter) {
			PreferenceStore.updateSetting('app.tickerFilter', JSON.parse(JSON.stringify(filter)));
		}
		getTickerBets().then(() => {
			filtering = false;
		});
	}

	function getSelectedForCriteria(type) {
		let selected = filter[type];
		return selected ? selected : [];
	}

	function setDatePeriod(datePeriodSelected) {
		let resetPage = false;
		if (datePeriodSelected !== selectedDatePeriod) {
			resetPage = true;
		}

		if (wantsCustomDateRange) {
			wantsCustomDateRange = false;
		}

		selectedDatePeriod = datePeriodSelected;
		if (datePeriodSelected == BetTickerPeriods.Custom) {
			wantsCustomDateRange = true;
		} else if (datePeriodSelected == BetTickerPeriods.Last7Days) {
			startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
			endDate = '';
		} else if (datePeriodSelected == BetTickerPeriods.Last30Days) {
			startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
			endDate = '';
		} else if (datePeriodSelected == BetTickerPeriods.Last90Days) {
			startDate = dayjs().subtract(90, 'day').format('YYYY-MM-DD');
			endDate = '';
		} else if (datePeriodSelected == BetTickerPeriods.Lifetime) {
			startDate = '';
			endDate = '';
		}

		updateDateFilter();

		if (!wantsCustomDateRange) {
			applyFilter(resetPage);
		}
	}

	function getSavedFilters() {
		AdminUserSavedFilterStore.getSavedFilters();
	}

	function manualResult(event, market, selection) {
		if (market) {
			resultMarket = market.id;
			resultEventName = event.name;
			showManualResult = true;
		} else {
			resultSelection = extractMiddleNumber(selection);
			resultEventName = event.name;
			showManualResultSGM = true;
		}
	}

	function setSavedFilter(savedFilter) {
		resetFilter();
		if (!savedFilter) {
			return;
		}
		const options = JSON.parse(savedFilter.options);
		for (const [filter_name, filter_value] of Object.entries(options)) {
			filter[filter_name] = filter_value;
		}
		applyFilter();
	}

	function deleteSavedFilter(selectedFilter) {
		selectedFilter
			.delete()
			.then(() => {
				selectedSavedFilter = false;
				getSavedFilters();
				notify({
					type: 'success',
					title: 'Saved filter removed',
					message: 'Saved filter has been removed!',
					timeout: 3
				});
			})
			.catch((e) => {
				console.error(e);
				notify({
					type: 'error',
					title: 'An error occurred',
					message: 'We were unable to delete this saved filter!',
					timeout: 3
				});
			});
	}
	const handle_keydown = (e) => {
		if (e.key === 'Enter') {
			loading = onSearchClick();
		}
	};
</script>

<Helmet title={displayFormat === 'cashout' ? 'Cashed out bets' : 'Bet ticker'} />

<Adjustment store={$BetTickerStore} on:saved={getTickerBets} />
<Cancel store={$BetTickerStore} on:saved={getTickerBets} />
<ReviewPayout store={$BetTickerStore} on:saved={getTickerBets} />
{#if searchBetTicket !== null}
	<Polling pollFunction={getTickerBets} frequency={90} />
{:else}
	<Polling pollFunction={onSearchClick} frequency={90} />
{/if}
<SaveFilter bind:open={showSaveFilter} {filter} />

{#if showManualResult}
	<ManualResult
		market_id={resultMarket}
		eventName={resultEventName}
		bind:open={showManualResult}
		on:resulted={getTickerBets}
	/>
{/if}
{#if showManualResultSGM}
	<ManualResultSGM
		selection_id={resultSelection}
		eventName={resultEventName}
		bind:open={showManualResultSGM}
		on:resulted={getTickerBets}
	/>
{/if}

<div>
	{#await loading}
		<Loading />
	{:then}
		<div>
			<!-- Page Header -->
			<div class="flex flex-col items-center w-full p-4 text-sm bg-white border-b md:py-2">
				<div class="flex md:flex-row flex-col items-center justify-between w-full">
					<h1 class="text-xl font-bold">{displayFormat === 'cashout' ? 'Cashed out bets' : 'Bet ticker'}</h1>
					<div class="flex flex-col md:flex-row items-center md:space-x-2">
						<div class="flex items-center md:space-x-2">
							<div class="flex flex-col md:flex-row items-center mr-2 md:mr-0">
								<span class="font-semibold">Date period: </span>
								<SelectInput className="ml-2" name="sport" required={true} bind:value={selectedDatePeriod}>
									{#each Object.entries(BetTickerPeriodNames) as BetTickerPeriod}
										{#if parseInt(BetTickerPeriod[0]) === BetTickerPeriods.Lifetime}
											{#if ACLStore.isSuper()}
												<option value={parseInt(BetTickerPeriod[0])}>{BetTickerPeriod[1]}</option>
											{/if}
										{:else}
											<option value={parseInt(BetTickerPeriod[0])}>{BetTickerPeriod[1]}</option>
										{/if}
									{/each}
								</SelectInput>
							</div>
							<div>
								<span class="font-semibold">Start Date:</span>
								<input
									type="date"
									bind:value={startDate}
									disabled={!wantsCustomDateRange}
									class="border-b border-row-separator focus:outline-none"
								/>
							</div>
							<div>
								<span class="font-semibold">End Date:</span>
								<input
									type="date"
									bind:value={endDate}
									disabled={!wantsCustomDateRange}
									class="border-b border-row-separator focus:outline-none"
								/>
							</div>
							{#if showFilter == false}
								<button on:click={() => applyFilter} class="h-8 btn btn-black"> Apply </button>
							{/if}
						</div>
						<div class="flex flex-row items-center mt-4 md:mt-0 md:space-x-2">
							<div class="flex flex-col md:flex-row items-center mr-4 md:mr-0">
								<span class="text-nowrap font-semibold">Per page:</span>
								<SelectInput bind:value={pageSize}>
									<option value={50}>50</option>
									<option value={100}>100</option>
									<option value={200}>200</option>
								</SelectInput>
							</div>

							{#if showSearch}
								<div class="flex flex-col md:flex-row items-start">
									<span class="block md:hidden text-nowrap font-semibold text-left justify-start items-start"
										>Search:</span
									>
									<div class="flex flex-row items-center space-x-2">
										<TextInput
											name="bet_ticket"
											bind:value={searchBetTicket}
											placeholder="Bet Ticket ID..."
											on:keydown={handle_keydown}
										/>
										<LoadingButton on:click={onSearchClick} className="btn btn-black" loading={searching}
											>Search</LoadingButton
										>
										<button on:click={onClearSearchClick} class="btn btn-muted"> Clear </button>
									</div>
								</div>
							{/if}
						</div>
					</div>
				</div>
			</div>
			<!-- /Page Header -->
			<div class="bg-gray-100 rounded pb-0.5">
				{#if searchBetTicket == null}
					<div class="w-full border-t-2" />

					{#if showFilter == true}
						<div
							transition:slide|local
							class="flex flex-wrap md:flex-row items-center justify-center space-x-1.5 w-full p-4 text-sm md:py-2 w-"
						>
							<div class="flex flex-row items-center">
								<SelectInput
									className="ml-2 w-48"
									name="saved_filter"
									style="border-radius: 12px"
									required={true}
									bind:value={selectedSavedFilter}
								>
									<option value={false} disabled selected>Select Filter</option>
									{#each $AdminUserSavedFilterStore.all() as savedFilter (savedFilter.id)}
										<option value={savedFilter}>{savedFilter.name}</option>
									{/each}
								</SelectInput>
							</div>
							{#if selectedSavedFilter}
								<LoadingButton
									on:click={() => deleteSavedFilter(selectedSavedFilter)}
									tabindex="-1"
									title="Remove saved filter"
									class="p-1 ml-1 text-white rounded bg-danger hover:bg-grey focus:outline-none"
								>
									<Icon name="bin" />
								</LoadingButton>
							{/if}

							{#each Object.entries(filterMeta ?? {}) as criteria, i (i)}
								<PillMultiSelect
									className="min-w-24"
									name={criteria[0]}
									label={criteria[1].name.includes('Bet')
										? criteria[1].name.replace(/\s*Bet\s*/g, '')
										: criteria[1].name}
									on:change={(e) => {
										updateFilter(e);
										selectedSavedFilter = false;
									}}
									singleSelection={criteria[0] === 'min_bet_amount'}
									options={Object.entries(criteria[1].values)}
									action={false}
									wrapValue={true}
									checked={getSelectedForCriteria(criteria[0])}
								/>
							{/each}
							<div class="flex flex-row items-center justify-center space-x-4 md:space-x-2 mt-2 md:mt-0">
								<button on:click={applyFilter} class="h-8 btn btn-black"> Apply </button>
								<button
									on:click={() => {
										resetFilter();
										selectedSavedFilter = false;
									}}
									class="h-8 btn btn-muted"
								>
									Reset
								</button>
								<button on:click={() => (showSaveFilter = true)} class="h-8 btn btn-primary"> Save </button>
							</div>
						</div>
					{/if}
				{/if}
				<div class="overflow-x-auto rounded">
					<table class="w-full">
						<thead>
							<tr class="text-xs text-left text-gray-500 uppercase">
								<th class="px-2 pt-5 pb-4 font-medium text-center">Ticket</th>
								<th class="px-2 pt-5 pb-4 font-medium text-center">Date</th>
								<th class="px-2 pt-5 pb-4 font-medium text-center">User</th>
								<th class="px-2 pt-5 pb-4 font-medium">Event</th>
								<th class="px-2 pt-5 pb-4 font-medium">Selection</th>
								<th class="px-2 pt-5 pb-4 font-medium">Bet</th>
								<th class="px-2 pt-5 pb-4 font-medium">Liability</th>
								<th class="px-2 pt-5 pb-4 font-medium">Return</th>
								{#if displayFormat === 'cashout'}
									<th class="px-2 pt-5 pb-4 font-medium">Cashed out At</th>
									<th class="px-2 pt-5 pb-4 font-medium">Price @ Cashout</th>
								{/if}
								<th class="px-2 pt-5 pb-4 font-medium">Net Win</th>
								<th class="px-2 pt-5 pb-4 font-medium text-center">Status</th>
								<th class="px-2 pt-5 pb-4 font-medium text-center" />
							</tr>
						</thead>
						{#if !filtering}
							<tbody class="bg-white">
								{#if !bets.count()}
									<tr>
										<td class="px-6 py-4 text-center border-t" colspan={displayFormat === 'cashout' ? 13 : 11}>
											No bets found.
										</td>
									</tr>
								{:else}
									{#each $BetTickerStore && bets.all() as bet (bet.id)}
										<tr class="hover:bg-gray-100 focus-within:bg-gray-100">
											<BetTickerRow
												{bet}
												on:updated={getTickerBets}
												cashoutFormat={displayFormat === 'cashout'}
												{manualResult}
											/>
										</tr>
									{/each}
								{/if}
							</tbody>
						{/if}
					</table>
				</div>
				{#if !filtering}
					<Pagination {links} on:click={changePage} className="my-3" />
				{:else}
					<Loading />
				{/if}
			</div>
		</div>
	{/await}
</div>
